import { Transaction, TransactionActivity, TransactionList } from '@kalos/kalos-rpc';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useAuth } from '../../../../context/AuthContext';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { useTransactionUpdateMutation } from '../../../../hooks/react-query/useTransactionQuery';
import { timestamp, TransactionActivityClientService } from '../../../../tools/helpers';

export const createActivityReq = (
  description: string,
  transactionId: number,
  loggedUserId: number,
) => {
  const activity = TransactionActivity.create();
  activity.isActive = 1;
  activity.timestamp = timestamp();
  activity.userId = loggedUserId;
  activity.description = description;
  activity.transactionId = transactionId;
  return activity;
};

export const useMakeTransactionActivityLog = () => {
  const loggedUserId = useAuth().user.id;
  const makeLog = useCallback(
    async (description: string, transactionId: number) => {
      const activity = createActivityReq(description, transactionId, loggedUserId);
      await TransactionActivityClientService.Create(activity);
    },
    [loggedUserId],
  );
  return useMemo(() => ({ makeLog }), [makeLog]);
};

export const useMakeUpdateTransactionStatus = ({
  shouldDeleteAfterUpdate,
}: { shouldDeleteAfterUpdate?: boolean } = {}) => {
  const { makeLog } = useMakeTransactionActivityLog();

  const transactionUpdateMutation = useTransactionUpdateMutation();
  const queryClient = useQueryClient();
  const makeUpdateStatus = useCallback(
    async ({
      id,
      statusID,
      description,
      reason,
    }: {
      id: Transaction['id'];
      statusID: Transaction['statusId'];
      description: string;
      reason?: string;
    }) => {
      const txn = Transaction.create();
      txn.id = id;
      txn.statusId = statusID;
      txn.fieldMask = ['StatusId'];
      txn.isBillingRecorded = true;

      await transactionUpdateMutation.mutateAsync(txn);
      if (shouldDeleteAfterUpdate) {
        queryClient.setQueriesData<TransactionList>(
          { queryKey: [queryKeys.transaction.root, queryKeys.transaction.list], type: 'active' },
          (cache) => {
            if (cache && cache.results.find((el) => el.id === txn.id)) {
              return TransactionList.create({
                results: cache.results.filter((entry) => entry.id !== txn.id),
                totalCount: cache.totalCount - 1,
              });
            }
          },
        );
        await queryClient.invalidateQueries({ queryKey: [queryKeys.transaction.root] });
      }

      try {
        await makeLog([description, reason].filter(Boolean).join(' '), id);
      } catch (err) {
        console.error(`An error occurred while making an activity log: ${err}`);
      }
    },
    [makeLog, queryClient, shouldDeleteAfterUpdate, transactionUpdateMutation],
  );

  return useMemo(() => ({ makeUpdateStatus }), [makeUpdateStatus]);
};
